import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Trans, useTranslation } from 'react-i18next';
import { Button, Callout, Checkbox, Modal, Skeleton } from '@/components/ui';
import { fetchRoleMapImpact } from '@/api/console';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { useAuth } from '@/auth/useAuth';
import styles from './RemoveLinkConfirmModal.module.css';

export interface RemoveLinkTarget {
    /** The `RoleMap` row id being removed. */
    id: string;
    /** Standard role KEY — what the write and the undo bookkeeping address. */
    roleKey: string;
    /** App role KEY — same. */
    appRole: string;
    /** Standard role, as the reader sees it. */
    roleName: string;
    /** App role, as the reader sees it. */
    appRoleName: string;
    appName: string;
}

/**
 * EAA-1817 (design frame 55g): confirm removing one link, stating who loses what.
 *
 * WHY THIS IS NEW AND NOT A POLISH. Removing a chip on the coverage board used to
 * happen on a single click with no confirmation — the only safety net was the 10s
 * undo banner afterwards. That is the wrong shape for this action: the operator
 * cannot see, before clicking, that 214 people are about to lose access to an
 * application, and "undo within ten seconds" only helps someone who already
 * realised they made a mistake. The list view has had a confirm since EAA-1837;
 * the board, which is where this edit actually happens, did not.
 *
 * THE COUNT IS SERVER-COMPUTED. `GET /role-maps/{id}/impact` returns DISTINCT
 * `user_sub`s (`account_count`), not assignment rows — a person holding the role
 * twice is one person losing access, and `assignment_count` would overstate it.
 *
 * The endpoint is gated on `ROLEMAP_VIEW` (`@require_permissions`, not a
 * superadmin check — an earlier version of this comment said `IsSuperAdmin` and
 * was simply wrong). It is still a DIFFERENT permission from the one that lets
 * you remove a link, so the number can legitimately be unavailable to a caller
 * who may perform the removal: the modal then states the consequence without a
 * figure rather than printing a zero that would read as "nobody is affected".
 * Same choice `RoleMapDeleteConfirmModal` makes.
 *
 * "KEEP EXISTING ACCOUNTS" IS OFFERED BUT NOT WIRED, and that is deliberate: the
 * frame draws the option, no endpoint implements it (a remove is a remove —
 * `DELETE /role-maps/{id}` soft-deletes the row and the next provisioning run
 * revokes), and shipping a checkbox that silently does nothing would be worse
 * than shipping none. It renders disabled with the reason attached. See the SRS.
 */
export function RemoveLinkConfirmModal({
    target,
    pending,
    resolveAppRoleName,
    onCancel,
    onConfirm,
}: {
    target: RemoveLinkTarget | null;
    pending: boolean;
    /**
     * app-role key → display name. `remaining_app_roles` comes back from the
     * impact endpoint as KEYS, and this dialog names every other role by its
     * display name — printing `sys-editor` in one line and "Editor" in the next
     * is the mixed vocabulary the 2026-08-24 design pass exists to remove.
     * Falls back to the key when the catalog has not loaded.
     */
    resolveAppRoleName: (key: string) => string;
    onCancel: () => void;
    onConfirm: () => void;
}) {
    const { t } = useTranslation();
    const config = useRuntimeConfig();
    const { accessToken } = useAuth();
    const [keepExisting, setKeepExisting] = useState(false);

    const impactQuery = useQuery({
        queryKey: ['role-map-impact', target?.id, accessToken],
        queryFn: () => fetchRoleMapImpact(config.apiBase, accessToken as string, target!.id),
        enabled: Boolean(target?.id && accessToken),
        retry: false,
    });

    if (!target) return null;

    const accountCount = impactQuery.data?.account_count ?? null;
    const remaining = impactQuery.data?.remaining_app_roles ?? [];

    return (
        <Modal
            open
            title={t('console.roleCoverage.removeConfirm.title', {
                roleName: target.roleName,
                appRoleName: target.appRoleName,
                appName: target.appName,
            })}
            onCancel={onCancel}
            footer={
                <>
                    <Button onClick={onCancel}>{t('common.cancel')}</Button>
                    {/* `danger`: this revokes access for real people. */}
                    <Button danger type="primary" disabled={pending} onClick={onConfirm}>
                        {t('console.roleCoverage.removeConfirm.confirm')}
                    </Button>
                </>
            }
        >
            <div className={styles.removeConfirm}>
                <p>
                    <Trans
                        i18nKey="console.roleCoverage.removeConfirm.body"
                        values={{ roleName: target.roleName, appName: target.appName }}
                        components={{ strong: <strong /> }}
                    />
                </p>

                {impactQuery.isLoading && <Skeleton height={44} />}

                {accountCount !== null && (
                    <Callout tone={accountCount > 0 ? 'danger' : 'neutral'}>
                        <Trans
                            i18nKey={
                                accountCount > 0
                                    ? 'console.roleCoverage.removeConfirm.impact'
                                    : 'console.roleCoverage.removeConfirm.impactNone'
                            }
                            values={{ count: accountCount, appName: target.appName }}
                            components={{ strong: <strong /> }}
                        />
                    </Callout>
                )}

                {/* The count is super-admin-only. Say so instead of printing a
                    zero that would read as "nobody is affected". */}
                {impactQuery.isError && (
                    <Callout tone="warning">
                        {t('console.roleCoverage.removeConfirm.impactUnavailable')}
                    </Callout>
                )}

                {remaining.length > 0 && (
                    <p className={styles.removeConfirmRemaining}>
                        {t('console.roleCoverage.removeConfirm.remaining', {
                            roles: remaining.map(resolveAppRoleName).join(', '),
                        })}
                    </p>
                )}

                <Checkbox
                    checked={keepExisting}
                    disabled
                    onChange={(event) => setKeepExisting(event.target.checked)}
                >
                    {t('console.roleCoverage.removeConfirm.keepExisting')}
                </Checkbox>
                <p className="portal-admin-hint">
                    {t('console.roleCoverage.removeConfirm.keepExistingUnavailable')}
                </p>

                <p className="portal-admin-hint">
                    {t('console.roleCoverage.removeConfirm.undoNote')}
                </p>
            </div>
        </Modal>
    );
}

export default RemoveLinkConfirmModal;
